import { PeriodFilter } from '@/components/ui/analytics/PeriodFilter';
import { SummaryMetricCard } from '@/components/ui/analytics/SummaryMetricCard';
import { Badge } from '@/components/ui/shadcn/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from '@/components/ui/shadcn/chart';
import { useChartHeightClass } from '@/hooks/use-chart-height';
import { useTranslations } from '@/hooks/useTranslations';
import { PRIORITY_COLORS, statusColor, WORK_ORDER_STATUS_COLORS } from '@/lib/chart-colors';
import { Link } from '@inertiajs/react';
import { AlertTriangle, CalendarClock, CheckCircle2, Clock3, ListChecks, UserX, Wrench } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, Cell, Legend, Pie, PieChart, XAxis, YAxis } from 'recharts';

interface ServiceOperationsDashboardProps {
    dateRange: {
        from_date: string;
        to_date: string;
    };
    serviceDashboardData: {
        kpis: {
            total: number;
            completed: number;
            inProgress: number;
            unscheduled: number;
            overdue: number;
        };
        statusBreakdown: Array<{ status: string; count: number; ratio: number }>;
        priorityBreakdown: Array<{ priority: string; count: number; ratio: number }>;
        trend: Array<{ label: string; created: number; completed: number }>;
        topWorkers: Array<{ id: string; name: string; avatar?: string | null; completedCount: number }>;
    };
}

function toSentenceCase(value: string): string {
    const normalized = value.replaceAll('_', ' ').trim().toLowerCase();
    if (!normalized) {
        return normalized;
    }

    return normalized[0].toUpperCase() + normalized.slice(1);
}

function getInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return '--';
    }
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function AvatarOrInitials({ name, avatar }: { name: string; avatar?: string | null }) {
    if (avatar) {
        return <img src={avatar} alt={name} className="h-7 w-7 rounded-full object-cover" />;
    }

    return (
        <span className="bg-primary/10 text-primary flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold">
            {getInitials(name)}
        </span>
    );
}

function EmptyState({ title }: { title: string }) {
    return (
        <div className="text-muted-foreground flex h-[280px] items-center justify-center gap-2 rounded-lg border border-dashed text-sm">
            <UserX className="h-4 w-4" />
            <span>{title}</span>
        </div>
    );
}

export function ServiceOperationsDashboard({ dateRange, serviceDashboardData }: ServiceOperationsDashboardProps) {
    const { t } = useTranslations();
    const { kpis, statusBreakdown, priorityBreakdown, trend, topWorkers } = serviceDashboardData;
    const chartHeightClass = useChartHeightClass('md');

    const completionRate = kpis.total > 0 ? (kpis.completed / kpis.total) * 100 : 0;

    const statusPieData = statusBreakdown.map((item) => ({
        name: t(`settings.work_order_statuses.${item.status}`, toSentenceCase(item.status)),
        value: item.count,
        ratio: item.ratio,
        fill: statusColor(WORK_ORDER_STATUS_COLORS, item.status),
    }));
    const statusChartConfig = statusPieData.reduce<Record<string, { label: string; color: string }>>((acc, entry) => {
        acc[entry.name] = { label: entry.name, color: entry.fill };
        return acc;
    }, {});

    return (
        <div className="space-y-6">
            <div className="flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
                <div>
                    <h1 className="text-3xl font-semibold">{t('homepage.service_operations.title', 'Service operations')}</h1>
                    <p className="text-muted-foreground text-sm">
                        {t('homepage.service_operations.subtitle', 'Service throughput, status mix, priorities and on-time delivery.')}
                    </p>
                </div>
                <PeriodFilter dateRange={dateRange} className="rounded-md border px-2 py-1.5" />
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
                <SummaryMetricCard
                    title={t('homepage.service_operations.statistics.total_work_orders', 'Total work orders')}
                    value={kpis.total}
                    subtitle={t('homepage.service_operations.statistics.selected_period', 'Selected period')}
                    icon={<Wrench className="h-4 w-4" />}
                />
                <SummaryMetricCard
                    title={t('homepage.service_operations.statistics.completed', 'Completed')}
                    value={kpis.completed}
                    subtitle={`${completionRate.toFixed(1)}% ${t('homepage.service_operations.statistics.completion_rate', 'completion rate')}`}
                    icon={<CheckCircle2 className="h-4 w-4" />}
                    intent="success"
                />
                <SummaryMetricCard
                    title={t('homepage.service_operations.statistics.in_progress', 'In progress')}
                    value={kpis.inProgress}
                    subtitle={t('homepage.service_operations.statistics.active_load', 'Active service load')}
                    icon={<Clock3 className="h-4 w-4" />}
                />
                <SummaryMetricCard
                    title={t('homepage.service_operations.statistics.unscheduled', 'Unscheduled')}
                    value={kpis.unscheduled}
                    subtitle={t('homepage.service_operations.statistics.awaiting_scheduling', 'Awaiting scheduling')}
                    icon={<CalendarClock className="h-4 w-4" />}
                />
                <SummaryMetricCard
                    title={t('homepage.service_operations.statistics.overdue', 'Overdue')}
                    value={kpis.overdue}
                    subtitle={t('homepage.service_operations.statistics.past_due', 'Past expected date')}
                    icon={<AlertTriangle className="h-4 w-4" />}
                    intent={kpis.overdue > 0 ? 'danger' : 'neutral'}
                />
            </div>

            {/* Trend + Status */}
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle>{t('homepage.service_operations.work_order_trend.title', 'Created vs Completed')}</CardTitle>
                        <CardDescription>
                            {t(
                                'homepage.service_operations.work_order_trend.description',
                                'Work orders created and completed over the selected period.',
                            )}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {trend.length === 0 ? (
                            <EmptyState title={t('homepage.service_operations.empty.no_work_orders', 'No work orders in selected range')} />
                        ) : (
                            <ChartContainer
                                role="img"
                                aria-label={t('homepage.service_operations.work_order_trend.title', 'Created vs Completed')}
                                config={{
                                    created: { label: t('homepage.service_operations.chart.created', 'Created'), color: '#2563eb' },
                                    completed: { label: t('homepage.service_operations.chart.completed', 'Completed'), color: '#16a34a' },
                                }}
                                className={`${chartHeightClass} w-full`}
                            >
                                <AreaChart accessibilityLayer data={trend} margin={{ left: 12, right: 12 }}>
                                    <defs>
                                        <linearGradient id="soFillCreated" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="var(--color-created)" stopOpacity={0.8} />
                                            <stop offset="95%" stopColor="var(--color-created)" stopOpacity={0.1} />
                                        </linearGradient>
                                        <linearGradient id="soFillCompleted" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="var(--color-completed)" stopOpacity={0.8} />
                                            <stop offset="95%" stopColor="var(--color-completed)" stopOpacity={0.1} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid vertical={false} />
                                    <XAxis
                                        dataKey="label"
                                        tickLine={false}
                                        axisLine={false}
                                        tickMargin={8}
                                        interval="preserveStartEnd"
                                        minTickGap={24}
                                    />
                                    <YAxis allowDecimals={false} domain={[0, 'auto']} tickLine={false} axisLine={false} tickMargin={8} width={32} />
                                    <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="dot" />} />
                                    <ChartLegend content={<ChartLegendContent />} />
                                    <Area
                                        dataKey="created"
                                        type="monotone"
                                        fill="url(#soFillCreated)"
                                        fillOpacity={0.4}
                                        stroke="var(--color-created)"
                                    />
                                    <Area
                                        dataKey="completed"
                                        type="monotone"
                                        fill="url(#soFillCompleted)"
                                        fillOpacity={0.4}
                                        stroke="var(--color-completed)"
                                    />
                                </AreaChart>
                            </ChartContainer>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('homepage.service_operations.status_breakdown.title', 'Status breakdown')}</CardTitle>
                        <CardDescription>
                            {t('homepage.service_operations.status_breakdown.description', 'Work orders by status in the selected period.')}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {statusPieData.length === 0 ? (
                            <EmptyState title={t('homepage.service_operations.empty.no_status_data', 'No status data in selected range')} />
                        ) : (
                            <ChartContainer
                                role="img"
                                aria-label={t('homepage.service_operations.status_breakdown.title', 'Status breakdown')}
                                config={statusChartConfig}
                                className={`${chartHeightClass} w-full`}
                            >
                                <PieChart>
                                    <ChartTooltip content={<ChartTooltipContent nameKey="name" />} />
                                    <Legend
                                        layout="vertical"
                                        align="right"
                                        verticalAlign="middle"
                                        iconType="circle"
                                        formatter={(value) => <span className="text-sm">{value}</span>}
                                    />
                                    <Pie data={statusPieData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={110} paddingAngle={2}>
                                        {statusPieData.map((entry, i) => (
                                            <Cell key={i} fill={entry.fill} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ChartContainer>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Priority + Top workers */}
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle>{t('homepage.service_operations.priority_breakdown.title', 'Priority breakdown')}</CardTitle>
                        <CardDescription>
                            {t('homepage.service_operations.priority_breakdown.description', 'How work orders are distributed across priorities.')}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {priorityBreakdown.length === 0 ? (
                            <EmptyState title={t('homepage.service_operations.empty.no_priority_data', 'No priority data in selected range')} />
                        ) : (
                            priorityBreakdown.map((item) => (
                                <div key={item.priority} className="space-y-1">
                                    <div className="text-muted-foreground flex items-center justify-between text-xs">
                                        <span>{t(`settings.work_order_priorities.${item.priority}`, toSentenceCase(item.priority))}</span>
                                        <span>
                                            {item.count} ({item.ratio}%)
                                        </span>
                                    </div>
                                    <div className="bg-muted h-2.5 w-full rounded-full">
                                        <div
                                            className="h-2.5 rounded-full"
                                            style={{
                                                width: `${Math.max(item.ratio, 3)}%`,
                                                backgroundColor: statusColor(PRIORITY_COLORS, item.priority),
                                            }}
                                        />
                                    </div>
                                </div>
                            ))
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle>{t('homepage.service_operations.top_workers.title', 'Top 5 service workers')}</CardTitle>
                            <Link href="/employees" className="text-primary text-sm font-medium hover:underline">
                                {t('homepage.analytics.view_all', 'View all')}
                            </Link>
                        </div>
                        <CardDescription>{t('homepage.analytics.by_completed_work_orders', 'By completed work orders')}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        {topWorkers.length === 0 ? (
                            <EmptyState
                                title={t('homepage.service_operations.empty.no_completed_assignments', 'No completed assignments in selected range')}
                            />
                        ) : (
                            topWorkers.map((worker, index) => (
                                <div key={worker.id} className="flex items-center justify-between rounded-lg border p-2.5">
                                    <div className="flex min-w-0 items-center gap-2.5">
                                        <span className="bg-muted text-muted-foreground flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold">
                                            {index + 1}
                                        </span>
                                        <AvatarOrInitials name={worker.name} avatar={worker.avatar} />
                                        <p className="truncate text-sm font-medium">{worker.name}</p>
                                    </div>
                                    <Badge variant="secondary">{worker.completedCount}</Badge>
                                </div>
                            ))
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Quick links */}
            <Card>
                <CardHeader>
                    <CardTitle className="inline-flex items-center gap-2">
                        <ListChecks className="text-muted-foreground h-4 w-4" />
                        {t('homepage.service_operations.quick_links.title', 'Quick links')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <Link href="/work-orders" className="hover:bg-muted rounded-lg border p-4 text-sm font-medium transition-colors">
                        {t('homepage.service_operations.quick_links.all_work_orders', 'All work orders')}
                    </Link>
                    <Link
                        href="/work-orders?status=in_progress"
                        className="hover:bg-muted rounded-lg border p-4 text-sm font-medium transition-colors"
                    >
                        {t('homepage.service_operations.statistics.in_progress', 'In progress')}
                    </Link>
                    <Link href="/work-orders?unscheduled=true" className="hover:bg-muted rounded-lg border p-4 text-sm font-medium transition-colors">
                        {t('homepage.service_operations.statistics.unscheduled', 'Unscheduled')}
                    </Link>
                    <Link href="/work-orders?status=completed" className="hover:bg-muted rounded-lg border p-4 text-sm font-medium transition-colors">
                        {t('homepage.service_operations.statistics.completed', 'Completed')}
                    </Link>
                </CardContent>
            </Card>
        </div>
    );
}
